import React, { useEffect, useState } from "react";
import { useRef, useCallback } from "react";
import {
  Dialog,
  IconButton,
  Box,
  Checkbox,
  Typography,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Button from "../../../ui/elements/buttons/Button";
import { ReactComponent as MappingIcon } from "../../../assets/svgs/mapping.svg";
import { User} from "../userType/User"
import { Exhibitor} from "../../exhibitor/exhibitorType/Exhibitor"
import Search from "../../../utils/Search";
import useUpdateExhibitor from "../../exhibitor/mutations/useUpdateExhibitor";
import { useNavigate } from "react-router-dom";
import { useUserContext } from "../UserContext";
import { useUsers } from "../queries/useUsers";
import CircularLoader from "../../../ui/elements/loader/CircularLoader";

// Define props for the MapUserPopup component
interface MapUserPopupProps {
  open: boolean;
  onClose: () => void;
  users: User[];
  exhibitors: Exhibitor[];
  userSearchText: string;
  setUserSearchText: (text: string) => void;
  exhibitorSearchText: string;
  setExhibitorSearchText: (text: string) => void;
  selectedUsers: number[];
  setSelectedUsers: React.Dispatch<React.SetStateAction<number[]>>;
  refetchUsers: () => void;
  isEdit?: boolean;
  fetchExhibitorsNextPage: () => void;
  hasExhibitorsNextPage: boolean;
  isFetchingExhibitorsNextPage: boolean;
  refetchExhibitors: () => void;
}


const MapUserPopup: React.FC<MapUserPopupProps> = ({ open, onClose, users, exhibitors, userSearchText, setUserSearchText, exhibitorSearchText, setExhibitorSearchText, selectedUsers, setSelectedUsers, refetchUsers, isEdit, fetchExhibitorsNextPage,hasExhibitorsNextPage,isFetchingExhibitorsNextPage, refetchExhibitors}) => {
 
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const [selectedExhibitors, setSelectedExhibitors] = useState<string[]>([]);
  const [prevSelectedExhibitors, setPrevSelectedExhibitors] = useState<string[]>([]);
  const [allListedExhibitors, setAllListedExhibitors] = useState<Exhibitor[]>(exhibitors);

  const { mutate: UpdateExhibitor } = useUpdateExhibitor();

  const [isMapping, setIsMapping] = useState(false);

  const navigate = useNavigate();

  const {user,isAdmin}=useUserContext();


  const {
  usersData: unmappedUsersData,
  refetch: refetchUnmappedUsers,
  isLoading: isUnmappedLoading
} = useUsers({
  page: 1,
  rowsPerPage: 1000, 
  searchText: userSearchText,
  unmappedOnly: !isEdit, 
});
  
const usersArray = (unmappedUsersData?.data?.data as unknown as User[]) ?? [];
  // Filter users and exhibitors based on search input
  const filteredUsers = usersArray.filter((users) =>{
    return (users?.documentId !== user?.documentId &&
    users.username?.toLowerCase().includes(userSearchText?.toLowerCase()))
  });

  const filteredExhibitors = exhibitors.filter((exhibitor) =>
    exhibitor.name?.toLowerCase().includes(exhibitorSearchText?.toLowerCase())
  );

 useEffect(() => {
  setAllListedExhibitors((prev) => {
    const merged = [...prev, ...exhibitors];
    const unique = Array.from(new Map(merged.map(item => [item.id, item])).values());
    return unique;
  });
}, [exhibitors]);

  const handleToggleUser = (
    event: React.ChangeEvent<HTMLInputElement>,
    value: number,
  ) => {
    const isChecked = event.target.checked;
    if (isChecked) {
      setSelectedUsers((prev) => [...prev, value]);
    } else {
      setSelectedUsers((prev) => prev.filter((user) => user !== value));
    }
  };

  const handleClose = () => {
    setSelectedExhibitors([]);
    setSelectedUsers([]);
    setUserSearchText("");
    setExhibitorSearchText("");
    onClose();
  };

  // console.log("sellleceduserrr",selectedUsers)
  
const handleMapUserClick = async () => {
  setIsMapping(true);

  const selectedUserObjects = usersArray.filter((u) =>
    selectedUsers.includes(u.id)
  );

  let atLeastOneSuccess = false;

  if (isEdit) {
    // ✅ Only update exhibitors that were added or removed
    const added = selectedExhibitors.filter(
      (id) => !prevSelectedExhibitors.includes(id)
    );
    const removed = prevSelectedExhibitors.filter(
      (id) => !selectedExhibitors.includes(id)
    );

    const changedExhibitors = Array.from(new Set([...added, ...removed]));

    for (const exhibitorId of changedExhibitors) {
      try {
        const exhibitor = allListedExhibitors.find((e) => e.documentId === exhibitorId);
        if (!exhibitor) continue;

        const existingUsers = exhibitor.users ?? [];

        let finalUserIds: number[] = [];

        if (added.includes(exhibitorId)) {
          // Add users
          const updatedUsersMap = new Map<number, User>();
          [...existingUsers, ...selectedUserObjects].forEach((user) =>
            updatedUsersMap.set(user.id, user)
          );
          finalUserIds = Array.from(updatedUsersMap.values()).map(
            (user) => user.id
          );
        } else if (removed.includes(exhibitorId)) {
          // Remove users
          finalUserIds = existingUsers
            .filter((user) => !selectedUsers.includes(user.id))
            .map((user) => user.id);
        }

        const updatedExhibitor = {
          data: {
            users: finalUserIds,
          },
        };

        await new Promise<void>((resolve) => {
          UpdateExhibitor(
            {
              exhibitorId,
              updatedExhibitor: updatedExhibitor as unknown as Exhibitor,
            },
            {
              onSuccess: () => {
                console.log("Exhibitor updated:", exhibitorId);
                atLeastOneSuccess = true;
                resolve();
                refetchExhibitors();
              },
              onError: (err) => {
                console.error("Failed to update exhibitor", exhibitorId, err);
                resolve();
              },
            }
          );
        });
      } catch (err) {
        console.error("Error updating exhibitor", exhibitorId, err);
      }
    }
  } else {
    // ✅ Initial mapping logic — add selected users to each selected exhibitor
    for (const exhibitorId of selectedExhibitors) {
      try {
        const exhibitor = allListedExhibitors.find((e) => e.documentId === exhibitorId);
        if (!exhibitor) continue;

        const existingUsers = exhibitor.users ?? [];

        const updatedUsersMap = new Map<number, User>();
        [...existingUsers, ...selectedUserObjects].forEach((user) =>
          updatedUsersMap.set(user.id, user)
        );

        const updatedExhibitor = {
          data: {
            users: Array.from(updatedUsersMap.values()).map((user) => user.id),
          },
        };

        await new Promise<void>((resolve) => {
          UpdateExhibitor(
            {
              exhibitorId,
              updatedExhibitor: updatedExhibitor as unknown as Exhibitor,
            },
            {
              onSuccess: () => {
                console.log("Exhibitor updated:", exhibitorId);
                atLeastOneSuccess = true;
                resolve();
              },
              onError: (err) => {
                console.error("Failed to update exhibitor", exhibitorId, err);
                resolve();
              },
            }
          );
        });
      } catch (err) {
        console.error("Error updating exhibitor", exhibitorId, err);
      }
    }
  }

  if (atLeastOneSuccess) {
    setIsMapping(false);
    refetchUsers();
    refetchUnmappedUsers();

  }

  handleClose();
};


const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;

    if (
      scrollTop + clientHeight >= scrollHeight - 50 &&
      hasExhibitorsNextPage &&
      !isFetchingExhibitorsNextPage
    ) {
      fetchExhibitorsNextPage();
    }
  }, [fetchExhibitorsNextPage, hasExhibitorsNextPage, isFetchingExhibitorsNextPage]);



 useEffect(() => {
  if (isEdit) {
    const exhibitorIds = users
      .filter((user) => selectedUsers.includes(user.id))
      .flatMap((user) => user.exhibitors?.map((ex) => ex.documentId) ?? []);

    const uniqueIds = Array.from(new Set(exhibitorIds));
    setSelectedExhibitors(uniqueIds);
    setPrevSelectedExhibitors(uniqueIds); // 🟢 Save initial snapshot
  }
}, [isEdit, selectedUsers, users]);



  return (
    <Dialog
      open={open}
      onClose={handleClose} 
      maxWidth="lg"
      fullWidth
      PaperProps={{
        style: {
          height: "549px",
          backgroundColor: "black",
          display: "flex",
          padding: "16px",
          paddingLeft: "32px",
          overflow: "visible",
        },
      }}
    >
      <Box padding={0} display="flex" justifyContent="flex-end" p={0}>
        <IconButton onClick={handleClose}>
          <CloseIcon sx={{ color: "white" }} />
        </IconButton>
      </Box>

      <Box
        display={{ sx: "block", md: "flex" }}
        alignItems={"flex-start"}
        gap={"8px"}
        sx={{
          overflowY: { xs: "auto", md: "unset" },
          maxHeight: { xs: "500px", md: "unset" },
          "&::-webkit-scrollbar": {
            display: "none", 
          },
          scrollbarWidth: "none", 
        }}
      >
        <Typography color="#fff" fontSize="32px" marginRight={"6px"}>
          {isEdit ? "Edit" : "Map"} User
        </Typography>

        {/* User Dropdown */}
        <Box sx={{ flex: 1 }}>
          {/* Custom Users Dropdown */}
          <Box
            sx={{
              backgroundColor: "white",
              maxHeight: "400px",
              overflowY: "auto",
              mt: 1,
              borderRadius: "4px",
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
              maxWidth: "385px",
            }}
          >
            {/* Search Bar for Users */}
           
            <Box
        sx={{width:{xs:'210px',sm:'220px', md:'317px'}, paddingRight:{xs:'12px',sm:'0px', md:'24px'}}}
        mt={2}
        ml={2}
      
      >
        <Search
            searchText={userSearchText}
            setSearchText={setUserSearchText}
          />
      </Box>
            {(isEdit
              ? filteredUsers.filter((user) => selectedUsers.includes(user.id))
              : filteredUsers
            )?.map((user) => (
              <Box
                key={user.id}
                sx={{ display: "flex", alignItems: "center", p: 1 }}
              >
                <Checkbox
                  sx={{ color: "black" }}
                  checked={selectedUsers.includes(user.id)} // Convert user.id to string
                  onChange={(event) => handleToggleUser(event, user.id)}
                  disabled={isEdit}
                />
                <Box display="flex" alignItems="center" ml={1}>
                  <Box
                    sx={{
                      width: 25,
                      height: 25,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "#f66",
                      color: "white",
                      marginRight: 2,
                    }}
                  >
                    {user.username.charAt(0).toUpperCase()}
                  </Box>
                  <Box>
                    <Typography variant="body1">{user.username}</Typography>
                    <Typography variant="body2">{user.email}</Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <IconButton sx={{mt:2}}>
          <MappingIcon/>
        </IconButton>


        {/* Exhibitor Dropdown */}
        <Box sx={{ flex: 1 }}>

          {/* Custom Exhibitors Dropdown */}
          <Box
            ref={scrollRef}
            onScroll={handleScroll}
            sx={{
              backgroundColor: "white",
              maxHeight: "400px",
              overflowY: "auto",
              OverflowX: "hidden",
              mt: 1,
              borderRadius: "4px",
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
              maxWidth: "385px", 
            }}
          >
            {/* Search Bar for Exhibitors */}
             <Box
        sx={{width:{xs:'210px',sm:'220px', md:'317px'}, paddingRight:{xs:'12px',sm:'0px', md:'24px'}}}
        mt={2}
        ml={2}
      
      >
        <Search
            searchText={exhibitorSearchText}
            setSearchText={setExhibitorSearchText}
          />
      </Box>
      {filteredExhibitors.map((exhibitor) => {
  const isChecked = selectedExhibitors.includes(exhibitor.documentId);
  return (
    <Box
      key={exhibitor.documentId}
      sx={{ display: "flex", alignItems: "center", p: 1 }}
    >
      <Checkbox
        sx={{ color: "black" }}
        checked={isChecked}
        onChange={(e) => {
          const checked = e.target.checked;
          setSelectedExhibitors((prev) =>
            checked
              ? [...prev, exhibitor.documentId]
              : prev.filter((id) => id !== exhibitor.documentId)
          );
        }}
      />
      <Box display="flex" alignItems="center">
        <Box
          sx={{
            width: 25,
            height: 25,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#f66",
            color: "white",
            marginRight: 2,
          }}
        >
          {exhibitor.name.charAt(0).toUpperCase()}
        </Box>
        <Typography variant="body1">{exhibitor.name}</Typography>
      </Box>
    </Box>
  );
})}

{isFetchingExhibitorsNextPage && (
    <Box display="flex" justifyContent="center" my={2}>
      <CircularLoader />
    </Box>
  )}

          </Box>
        </Box>

        <Button
          onClick={handleMapUserClick}
          disabled={ !isEdit && (selectedUsers.length === 0 || selectedExhibitors.length === 0)}
          sx={{
            marginTop:1,
            marginLeft: "20px",
            width: "127px",
            height: "40px",
            borderRadius: "25px",
            padding: "13px 20px",
            fontSize: "14px",
          }}
        >
         {isMapping ? <CircularProgress size={20} color="inherit" /> : "Map User"}
        </Button>
      </Box>
    </Dialog>
  );
};

export default MapUserPopup;
